import { useRefract } from "refract-rxjs";

import { type MetricField } from "../../gql-generated/types";
import { type ApolloResult } from "../../shared/models";
import { type AudienceStatsInput, type AudienceStatsOutput, audienceStatsEffect, defaults } from "../effects/audience";
import { audienceDimensions, audienceFieldsWithDimensions, createFieldListWithDimensions } from "../models";

import { useDimensionBreakdownQuery } from "./useDimensionBreakdownQuery";
import { useDimensionBreakdownQueryParams } from "./useDimensionBreakdownQueryParams";
import { useOrganicStats } from "./useOrganicStats";

export const METRIC_MIN_LIMIT = 30;

const fields = createFieldListWithDimensions(audienceDimensions, audienceFieldsWithDimensions);

export const useAudienceStats = (baseMetricField: MetricField): ApolloResult<AudienceStatsOutput> => {
    const { chartsMetrics, error: chartsError, loading: chartsLoading } = useOrganicStats();
    const totalMetric = chartsMetrics[baseMetricField]?.totalValue;

    const dimensionBreakdownParams = useDimensionBreakdownQueryParams(fields);
    const {
        data: dimensionBreakdownData,
        error: dimensionBreakdownError,
        loading: dimensionBreakdownLoading,
    } = useDimensionBreakdownQuery(dimensionBreakdownParams);

    const metricsData = useRefract<AudienceStatsInput, AudienceStatsOutput>(audienceStatsEffect, {
        dimensionBreakdownData,
        dimensionBreakdownLoading,
        baseMetricField,
    });

    if (totalMetric && totalMetric >= METRIC_MIN_LIMIT) {
        return {
            loading: chartsLoading || dimensionBreakdownLoading,
            error: chartsError ?? dimensionBreakdownError,
            ...metricsData,
        };
    }

    return {
        loading: chartsLoading || dimensionBreakdownLoading,
        error: chartsError ?? dimensionBreakdownError,
        ...defaults,
    };
};
